import React from 'react';

// ==================== TYPES ====================

export type HMIComponentType =
  | 'tank'
  | 'pipe-h'
  | 'pipe-v'
  | 'pipe-elbow-l'
  | 'pipe-elbow-r'
  | 'valve'
  | 'pump'
  | 'motor'
  | 'sensor'
  | 'label'
  | 'line'
  | 'image'
  | 'arrow'
  | 'tank-h'
  | 'motor-industrial'
  | 'valve-fcv'
  | 'nav-button'
  | 'agitator';

export interface HMIComponent {
  id: string;
  type: HMIComponentType;
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  plcId?: string;
  tagName?: string;
  minValue?: number;
  maxValue?: number;
  onValue?: number;
  unit?: string;
  color?: string;
  alertHigh?: number;
  alertLow?: number;
  fontSize?: number;
  sensorType?: string;
  rotation?: number;
  /** Preset fluid coloring for pipes/tanks — 'custom' (default) falls back to `color`. */
  fluidType?: string;
  /** Tank only — whether this tank has a real Level Transmitter (LT) bound to it.
   *  Defaults to true (undefined = has one) so existing saved tanks keep their gauge.
   *  When false, the fill/sight-glass/alarm lights/value readout are all hidden — just the vessel shows. */
  hasLevelSensor?: boolean;
  /** Pipe types only — id of another component in the same layout whose ON/OFF state
   *  gates this pipe's flow animation (e.g. "this pipe only flows when that valve is
   *  open"), instead of needing its own tag binding. Ignored for non-pipe types. */
  flowSourceId?: string;
  /** Color of the physical housing/shell (tank body, pipe body, valve casing, etc.) —
   *  independent of `color`/`fluidType`, which control the liquid/flow/status accents.
   *  Defaults to a neutral steel gray when unset. */
  shellColor?: string;
  /** Color of the tag/name label text (and the standalone Label widget's text).
   *  Defaults to each widget's own built-in label color when unset. */
  labelColor?: string;
  /** Manual offset for the tag/name label, relative to its default position — lets it be
   *  dragged/nudged anywhere so it's never hidden behind the widget's own graphics. */
  labelDx?: number;
  labelDy?: number;
  /** Rotation (degrees) applied to the tag/name label text only, independent of the
   *  widget's own `rotation` — lets the label be kept upright/legible even when the
   *  icon it's attached to is rotated, or angled on its own. Defaults to 0. */
  labelRotation?: number;
  /** Line/Arrow widgets only — stroke thickness in px. Defaults to 3 when unset. */
  lineThickness?: number;
  /** Line/Arrow widgets only — 'solid' (default), 'dashed', or 'dotted'. */
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  /** Font size for the small status readout (RUNNING/STOPPED, OPEN/CLOSED, level %,
   *  etc.) shown by tank/valve/pump/motor widgets — independent of the tag/name
   *  label's own font size. Defaults to 9 when unset. */
  statusFontSize?: number;
  /** Pipe types only — reverses which way the flow animation travels (e.g. fluid
   *  running right-to-left through a horizontal pipe) without needing to rotate or
   *  otherwise flip the whole pipe. Defaults to forward (false/unset). */
  flowReverse?: boolean;
  /** Manual offset for the status readout (RUNNING/STOPPED, OPEN/CLOSED, level %,
   *  value/unit) — lets it be dragged/nudged anywhere, same as labelDx/labelDy does
   *  for the tag/name label. */
  statusDx?: number;
  statusDy?: number;
  /** Image widget only — the uploaded picture, as a data: URI (read client-side via
   *  FileReader and saved inline in the layout JSON — there's no separate image/asset
   *  upload endpoint in this app, so this keeps the picture traveling with the layout). */
  imageUrl?: string;
  /** Image widget only — 'contain' (default, keeps aspect ratio) or 'stretch' (fills
   *  the box exactly, ignoring aspect ratio). */
  imageFit?: 'contain' | 'stretch';
  /** Nav Button only — id of the other saved HMI layout (screen) this button jumps
   *  to when clicked in the Viewer. The special value '__back__' means "return to
   *  whichever screen was open before this one" instead of a fixed target. Unset =
   *  button is drawn but does nothing yet (not configured). */
  navTargetLayoutId?: string;
}

export interface HMILayout {
  width: number;
  height: number;
  background: string;
  gridSize: number;
  components: HMIComponent[];
}

export interface SavedHMILayout {
  id: string;
  name: string;
  clientId?: string;
  layout: HMILayout;
  sortOrder?: number;
  updatedAt?: string;
}

// ==================== CONSTANTS ====================

export const CANVAS_W = 1200;
export const CANVAS_H = 700;
export const GRID = 20;

export const COMPONENT_DEFAULTS: Record<HMIComponentType, { width: number; height: number }> = {
  tank:     { width: 100, height: 160 },
  'pipe-h': { width: 160, height: 30  },
  'pipe-v': { width: 30,  height: 160 },
  'pipe-elbow-l': { width: 70, height: 70 },
  'pipe-elbow-r': { width: 70, height: 70 },
  valve:    { width: 70,  height: 90  },
  pump:     { width: 100, height: 110 },
  motor:    { width: 100, height: 90  },
  sensor:   { width: 70,  height: 26  },
  label:    { width: 160, height: 32  },
  line:     { width: 160, height: 20  },
  image:    { width: 160, height: 120 },
  arrow:    { width: 160, height: 20  },
  'tank-h': { width: 200, height: 90  },
  'motor-industrial': { width: 150, height: 100 },
  'valve-fcv': { width: 170, height: 110 },
  'nav-button': { width: 150, height: 44 },
  agitator: { width: 90,  height: 170 },
};

// Preset fluid colors — pick a fluid type on a pipe/tank instead of hand-picking a hex color.
// 'custom' means "use the Accent Color picker instead" (the pre-existing behavior).
export const FLUID_TYPES: { value: string; label: string; color: string | null }[] = [
  { value: 'custom',   label: 'Custom Color', color: null      },
  { value: 'water',    label: 'Water',        color: '#3b82f6' },
  { value: 'oil',      label: 'Oil',          color: '#eab308' },
  { value: 'steam',    label: 'Steam',        color: '#e2e8f0' },
  { value: 'gas',      label: 'Gas',          color: '#f97316' },
  { value: 'chemical', label: 'Chemical',     color: '#a855f7' },
];

export const FLUID_COLORS: Record<string, string> = Object.fromEntries(
  FLUID_TYPES.filter(f => f.color).map(f => [f.value, f.color as string])
);

export const PALETTE_ITEMS: {
  type: HMIComponentType;
  label: string;
  desc: string;
  defaultLabel: string;
}[] = [
  { type: 'tank',    label: 'Tank',       desc: 'Storage vessel with level', defaultLabel: 'TK-01'  },
  { type: 'tank-h',  label: 'Horizontal Tank', desc: 'Horizontal vessel / drum with level', defaultLabel: 'TK-H01' },
  { type: 'pipe-h',  label: '→ Pipe (H)', desc: 'Horizontal pipe / flow',   defaultLabel: ''       },
  { type: 'pipe-v',  label: '↓ Pipe (V)', desc: 'Vertical pipe / flow',     defaultLabel: ''       },
  { type: 'pipe-elbow-l', label: '↰ Pipe Curve (L)', desc: 'Elbow bend — curves left',  defaultLabel: '' },
  { type: 'pipe-elbow-r', label: '↱ Pipe Curve (R)', desc: 'Elbow bend — curves right', defaultLabel: '' },
  { type: 'valve',   label: 'Valve',      desc: 'On/off isolation valve',    defaultLabel: 'XV-01'  },
  { type: 'valve-fcv', label: 'Flow Control Valve', desc: '0-100% modulating valve position', defaultLabel: 'FCV-01' },
  { type: 'pump',    label: 'Pump',       desc: 'Centrifugal pump',          defaultLabel: 'P-01'   },
  { type: 'motor',   label: 'Motor',      desc: 'Electric motor',            defaultLabel: 'M-01'   },
  { type: 'motor-industrial', label: 'Motor (Pump Style)', desc: 'Green when running, red when stopped', defaultLabel: 'M-01' },
  { type: 'agitator', label: 'Agitator', desc: 'Mixer motor with rotating impeller — drop on top of a tank', defaultLabel: 'AG-01' },
  { type: 'sensor',  label: 'Sensor',     desc: 'PT / TT / FT / LT …',      defaultLabel: 'PT-01'  },
  { type: 'label',   label: 'Label',      desc: 'Static text',               defaultLabel: 'Label'  },
  { type: 'line',    label: 'Line',       desc: 'Straight line / connector', defaultLabel: ''       },
  { type: 'image',   label: 'Image',      desc: 'Upload a picture',          defaultLabel: ''       },
  { type: 'arrow',   label: 'Arrow',      desc: 'Directional / flow arrow',  defaultLabel: ''       },
  { type: 'nav-button', label: 'Nav Button', desc: 'Click to jump to another screen, or go back', defaultLabel: 'Next Screen' },
];

// Resolves a component's effective ON/OFF state, following a chain of `flowSourceId`
// references (e.g. pipe -> pipe -> valve) so a whole run of pipes can gate off one
// valve upstream, not just the pipe directly touching it. `visited` guards against an
// accidental circular reference (A depends on B depends on A) — a cycle just falls
// back to that node's own value instead of recursing forever.
export function resolveFlowSourceOn(
  comp: HMIComponent,
  components: HMIComponent[],
  getRawValue: (c: HMIComponent) => number | null,
  visited: Set<string> = new Set(),
): boolean {
  if (!visited.has(comp.id)) {
    visited.add(comp.id);
    if (comp.flowSourceId) {
      const src = components.find(c => c.id === comp.flowSourceId);
      if (src) return resolveFlowSourceOn(src, components, getRawValue, visited);
    }
  }
  const v = getRawValue(comp);
  return v != null && v >= (comp.onValue ?? 1);
}

// Same flowSourceId-chasing as resolveFlowSourceOn above, but returns a continuous
// 0-1 intensity instead of a boolean — so a pipe fed by a Flow Control Valve (or any
// other analog source) can visibly speed up/brighten as the source's value rises
// from 0 to 100, instead of just snapping instantly to full-speed flow the moment
// it crosses the on/off threshold.
export function resolveFlowSourceIntensity(
  comp: HMIComponent,
  components: HMIComponent[],
  getRawValue: (c: HMIComponent) => number | null,
  visited: Set<string> = new Set(),
): number {
  if (!visited.has(comp.id)) {
    visited.add(comp.id);
    if (comp.flowSourceId) {
      const src = components.find(c => c.id === comp.flowSourceId);
      if (src) return resolveFlowSourceIntensity(src, components, getRawValue, visited);
    }
  }
  const v = getRawValue(comp);
  if (v == null) return 0;

  // Only a genuinely continuous/modulating source (the Flow Control Valve) gets a
  // proportional 0-1 intensity. Binary sources (on/off valve, pump, motor, or a
  // pipe itself) just report full intensity once their own on/off threshold is
  // met — otherwise a plain digital 0/1 tag would read as "1% open" against the
  // widget's default 0-100 range and make the flow look almost invisible/frozen.
  const isContinuousSource = comp.type === 'valve-fcv';
  if (!isContinuousSource) {
    return v >= (comp.onValue ?? 1) ? 1 : 0;
  }

  const min = comp.minValue ?? 0;
  const max = comp.maxValue ?? 100;
  if (max === min) return v >= (comp.onValue ?? 1) ? 1 : 0;
  return Math.max(0, Math.min(1, (v - min) / (max - min)));
}

// ==================== SVG DEFS (shared per render, keyed by comp.id) ====================

function safeid(id: string) {
  return id.replace(/[^a-zA-Z0-9]/g, '');
}

// Lightens a hex color toward white by `amount` (0 = unchanged, 1 = white) — used to
// derive a light, tinted metal palette for a widget's shell from its accent color,
// so picking a color actually recolors the housing/body, not just the liquid/status bits.
// Dash pattern for Line/Arrow widgets — scales with stroke thickness so dashes/dots
// stay proportional whether the line is thin or thick.
function lineDashArray(style: 'solid' | 'dashed' | 'dotted' | undefined, thickness: number): string | undefined {
  if (style === 'dashed') return `${thickness * 3} ${thickness * 2}`;
  if (style === 'dotted') return `0.1 ${Math.max(6, thickness * 2.4)}`;
  return undefined;
}

function tint(hex: string, amount: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return hex;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return `#${[mix(r), mix(g), mix(b)].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

// Blends two hex colors — used by the Flow Control Valve to sweep its position
// indicator smoothly from closed (red) to open (green) across 0–100%, instead of
// only ever showing two fixed states like the on/off ball valve does.
function blendColor(hexA: string, hexB: string, t: number): string {
  const clamp = Math.max(0, Math.min(1, t));
  const ma = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexA || '');
  const mb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexB || '');
  if (!ma || !mb) return hexA;
  const mix = (a: string, b: string) => Math.round(parseInt(a, 16) + (parseInt(b, 16) - parseInt(a, 16)) * clamp);
  return `#${[mix(ma[1], mb[1]), mix(ma[2], mb[2]), mix(ma[3], mb[3])].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

// Lets the builder make a widget's tag/name label its own drag/click target,
// independent of the widget body. The viewer passes nothing — the saved
// labelDx/labelDy offset still applies, it just isn't interactive there.
export interface HMILabelInteraction {
  onLabelMouseDown?: (e: React.MouseEvent) => void;
  selected?: boolean;
  // Same idea as the label above, but for the status readout (RUNNING/STOPPED,
  // OPEN/CLOSED, level %, value/unit) — lets it be dragged independently too.
  onStatusMouseDown?: (e: React.MouseEvent) => void;
  statusSelected?: boolean;
}

function labelGroupProps(interactive?: HMILabelInteraction) {
  return interactive?.onLabelMouseDown
    ? { onMouseDown: interactive.onLabelMouseDown, style: { cursor: 'move' } as React.CSSProperties }
    : {};
}

// Tag/name label transform: drag offset plus its own independent rotation, spun
// around the label's own text anchor (ax,ay) so it turns in place instead of
// swinging around the widget's origin — independent of the widget's own `rotation`.
function labelTransform(dx: number, dy: number, rotation: number | undefined, ax: number, ay: number) {
  const rot = rotation ?? 0;
  return rot !== 0
    ? `translate(${dx},${dy}) rotate(${rot},${ax},${ay})`
    : `translate(${dx},${dy})`;
}

function statusGroupProps(interactive?: HMILabelInteraction) {
  return interactive?.onStatusMouseDown
    ? { onMouseDown: interactive.onStatusMouseDown, style: { cursor: 'move' } as React.CSSProperties }
    : {};
}

// Rough dashed highlight box around a label so it's clear it's the active drag
// target — doesn't need to be pixel-perfect, just enough to read as "selected".
function LabelSelectionBox({
  x, y, text, fontSize, textAnchor = 'middle',
}: { x: number; y: number; text: string; fontSize: number; textAnchor?: 'start' | 'middle' | 'end' }) {
  const w = Math.max(String(text || '').length, 1) * fontSize * 0.62;
  const h = fontSize * 1.3;
  const left = textAnchor === 'middle' ? x - w / 2 : textAnchor === 'end' ? x - w : x;
  return (
    <rect x={left - 3} y={y - fontSize * 1.05} width={w + 6} height={h} rx={2}
      fill="none" stroke="#3b82f6" strokeWidth={1} strokeDasharray="3 2" style={{ pointerEvents: 'none' }} />
  );
}

// ==================== COMPONENT RENDERER ====================

export function renderHMIComponent(
  comp: HMIComponent,
  value: number | null = null,
  labelInteraction?: HMILabelInteraction,
  // Pipe types can mirror another component's ON/OFF state instead of their own tag
  // binding (e.g. "only flows when this valve is open") — the caller resolves that
  // other component's value and passes the computed on/off state in here.
  flowSourceOn?: boolean,
  // Continuous 0-1 version of the same flow-source resolution — lets a pipe fed by
  // a Flow Control Valve (or any analog source) visibly speed up/brighten as the
  // source rises from 0-100%, instead of snapping straight to full-speed flow.
  // Defaults to full intensity so pipes without an analog source behave exactly
  // as before.
  flowIntensity?: number,
): React.ReactNode {
  const { width: W, height: H, label, type } = comp;
  const val     = value ?? 0;
  // A fluid type (water/oil/steam/…) overrides the manual accent color, so pipes/tanks
  // read as the right fluid at a glance instead of needing a hand-picked hex value.
  const color   = (comp.fluidType && FLUID_COLORS[comp.fluidType]) || comp.color || '#3b82f6';
  const unit    = comp.unit  || '';
  const minVal  = comp.minValue ?? 0;
  const maxVal  = comp.maxValue ?? 100;
  const onVal   = comp.onValue  ?? 1;
  const uid     = safeid(comp.id);
  const fs      = comp.fontSize ?? 10; // user-controlled font size
  const sfs     = comp.statusFontSize ?? 9; // status readout (RUNNING/STOPPED/OPEN/CLOSED/level %) font size
  const stDx    = comp.statusDx ?? 0; // status readout drag offset, same idea as labelDx/labelDy
  const stDy    = comp.statusDy ?? 0;
  // Pipes only — lets flow run "backwards" through a pipe (e.g. right-to-left
  // instead of left-to-right) without rotating/flipping the whole pipe.
  // Defaults to full intensity (1) so a pipe with no analog flow source, or a
  // binary one (valve/pump/motor), animates at the same constant speed as before.
  const flowIntensityValue = flowIntensity ?? 1;
  const flowAnimStyle: React.CSSProperties = {
    animation: `hmi-flow-r ${(0.9 / Math.max(0.08, flowIntensityValue)).toFixed(2)}s linear infinite`,
    animationDirection: comp.flowReverse ? 'reverse' : 'normal',
  };
  // Same intensity scales how bright/visible the flow stroke reads — a trickle at
  // 10% open looks faint, full flow at 100% reads fully vivid.
  const flowOpacityValue = 0.75 * Math.max(0.15, flowIntensityValue);
  const labelDx = comp.labelDx ?? 0;
  const labelDy = comp.labelDy ?? 0;
  const labelRotation = comp.labelRotation ?? 0;

  // Shell palette — a light, tinted-metal look derived from the widget's own Component
  // Color (independent of the fluid/status `color` above, so recoloring the housing
  // never also recolors the liquid/flow/open-closed accents).
  const shellSource   = comp.shellColor || '#94a3b8'; // neutral steel gray until customized
  const shellLightest = tint(shellSource, 0.94); // brightest highlight band
  const shellLighter   = tint(shellSource, 0.84);
  const shellMid       = tint(shellSource, 0.70);
  const shellBase      = tint(shellSource, 0.48);
  const shellEdge      = tint(shellSource, 0.18); // stroke/outline — keeps definition

  const pct = maxVal !== minVal
    ? Math.max(0, Math.min(1, (val - minVal) / (maxVal - minVal)))
    : 0;

  const isOn       = flowSourceOn !== undefined ? flowSourceOn : (value != null && val >= onVal);
  const isAlarmHi  = comp.alertHigh != null && value != null && val >= comp.alertHigh;
  const isAlarmLo  = comp.alertLow  != null && value != null && val <= comp.alertLow;
  const isAlarm    = isAlarmHi || isAlarmLo;
  const statusColor = isAlarm ? '#ef4444' : color;

  // ───── TANK ─────────────────────────────────────────────────────────────────
  if (type === 'tank') {
    const cx = W / 2;
    // If the client has no Level Transmitter on this tank, don't show any level-derived
    // instrumentation (fill, sight glass, alarm lights, value/percentage) — just the vessel.
    const showLevel = comp.hasLevelSensor !== false;

    // ── Geometry ── A plain rounded-cap cylinder on a short tapered plinth. The
    // previous version spent most of its height on a flange ring + separate domed
    // manhole + tall bolted pedestal, which squeezed the actual liquid-bearing body
    // into a thin strip and let the tag label collide with the dome. This gives the
    // body the bulk of the vertical space so the fill level reads at a glance.
    const bodyX   = W * 0.12;
    const bodyW   = W * 0.76;
    const bodyTop = H * 0.15;
    const bodyBot = H * 0.80;
    const bodyH   = bodyBot - bodyTop;
    const ell     = Math.max(2.5, bodyW * 0.10);   // ellipse depth (3-D cap effect)

    // Liquid fill
    const fillH = bodyH * pct;
    const fillY = bodyBot - fillH;

    // Plinth (short tapered base) — one shape instead of the old fins/column/ring/plate stack.
    // Flush with the full body width at the top (so it sits seamlessly under the bottom
    // cap ellipse with no visible step) and only tapers mildly, so it reads as a solid
    // stand rather than a funnel.
    const legTop    = bodyBot;
    const legBottom = H * 0.95;
    const plinthTopW = bodyW;
    const plinthBotW = bodyW * 0.66;

    // Wave params
    const waveCycles = Math.ceil((bodyW + 200) / 40) + 2;
    const waveStartX = bodyX - 120;
    const waveAmp    = Math.max(1.5, Math.min(6, fillH * 0.08));

    return (
      <g>
        <defs>
          {/* Cylinder metallic body */}
          <linearGradient id={`tb-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor={shellBase} />
            <stop offset="18%"  stopColor={shellMid} />
            <stop offset="42%"  stopColor={shellLighter} />
            <stop offset="55%"  stopColor={shellLightest} stopOpacity="0.9" />
            <stop offset="68%"  stopColor={shellLighter} />
            <stop offset="82%"  stopColor={shellMid} />
            <stop offset="100%" stopColor={shellBase} />
          </linearGradient>
          {/* Water fill gradient */}
          <linearGradient id={`wd-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={statusColor} stopOpacity="0.80" />
            <stop offset="60%"  stopColor={statusColor} stopOpacity="0.55" />
            <stop offset="100%" stopColor={statusColor} stopOpacity="0.25" />
          </linearGradient>
          {/* Glass shine overlay */}
          <linearGradient id={`gs-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="white" stopOpacity="0.00" />
            <stop offset="28%"  stopColor="white" stopOpacity="0.06" />
            <stop offset="48%"  stopColor="white" stopOpacity="0.14" />
            <stop offset="62%"  stopColor="white" stopOpacity="0.06" />
            <stop offset="100%" stopColor="white" stopOpacity="0.00" />
          </linearGradient>
          {/* Clip to cylinder fill area */}
          <clipPath id={`tc-${uid}`}>
            <rect x={bodyX + 1} y={bodyTop + 1} width={bodyW - 2} height={bodyH - 2} />
          </clipPath>
          {/* Plinth gradient */}
          <linearGradient id={`bg-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor={shellBase} />
            <stop offset="40%"  stopColor={shellMid} />
            <stop offset="60%"  stopColor={shellLighter} />
            <stop offset="100%" stopColor={shellBase} />
          </linearGradient>
        </defs>

        {/* ══ PLINTH ══ */}
        <polygon
          points={`${cx - plinthTopW / 2},${legTop} ${cx + plinthTopW / 2},${legTop} ${cx + plinthBotW / 2},${legBottom} ${cx - plinthBotW / 2},${legBottom}`}
          fill={`url(#bg-${uid})`} stroke={shellEdge} strokeWidth={1} />
        <line x1={cx - plinthBotW / 2} y1={legBottom} x2={cx + plinthBotW / 2} y2={legBottom} stroke={shellEdge} strokeWidth={1.4} />

        {/* ══ CYLINDER BODY ══ */}
        {/* Main body */}
        <rect x={bodyX} y={bodyTop} width={bodyW} height={bodyH}
          fill={`url(#tb-${uid})`} />

        {/* Liquid fill */}
        {showLevel && pct > 0.005 && (
          <g clipPath={`url(#tc-${uid})`}>
            <rect x={bodyX + 1} y={fillY} width={bodyW - 2} height={fillH}
              fill={`url(#wd-${uid})`} />
            {fillH > 8 && (
              <>
                <path d={waveD(waveStartX, fillY + 1, waveAmp, waveCycles, bodyBot)}
                  fill={statusColor} fillOpacity={0.50}
                  style={{ animation: 'hmi-water-w1 3.2s linear infinite' }} />
                <path d={waveD(waveStartX - 16, fillY + 2, waveAmp * 0.65, waveCycles, bodyBot)}
                  fill="white" fillOpacity={0.12}
                  style={{ animation: 'hmi-water-w3 1.8s linear infinite' }} />
              </>
            )}
          </g>
        )}

        {/* Glass shine overlay */}
        <rect x={bodyX} y={bodyTop} width={bodyW} height={bodyH} fill={`url(#gs-${uid})`} />

        {/* Cylinder side outlines */}
        <line x1={bodyX}          y1={bodyTop} x2={bodyX}          y2={bodyBot} stroke={shellEdge} strokeWidth={1.2} />
        <line x1={bodyX + bodyW}  y1={bodyTop} x2={bodyX + bodyW}  y2={bodyBot} stroke={shellEdge} strokeWidth={1.2} />

        {/* Bottom cap ellipse */}
        <ellipse cx={cx} cy={bodyBot} rx={bodyW / 2} ry={ell}
          fill={shellMid} stroke={shellEdge} strokeWidth={1.2} />

        {/* Top cap ellipse — same width as the body, no overhanging flange/dome */}
        <ellipse cx={cx} cy={bodyTop} rx={bodyW / 2} ry={ell}
          fill={shellLighter} stroke={shellEdge} strokeWidth={1.4} />

        {showLevel && (
          <>
            {/* ══ STATUS INDICATORS (right side, inside the vessel width) ══ */}
            {[0, 1, 2].map(i => {
              const iy = bodyTop + bodyH * (0.20 + i * 0.11);
              const active = i === 0 ? isAlarmHi : i === 1 ? pct > 0.45 : isAlarmLo;
              const ic = i === 0 ? '#ef4444' : i === 1 ? statusColor : '#f59e0b';
              return (
                <circle key={i}
                  cx={bodyX + bodyW - W * 0.09} cy={iy} r={2.6}
                  fill={active ? ic : '#e2e8f0'}
                  stroke={ic} strokeWidth={0.8}
                  fillOpacity={active ? 0.9 : 0.3}
                />
              );
            })}

            {/* ══ VALUE DISPLAY (draggable as one group) — centered in the body,
                well clear of the plinth ══ */}
            <g transform={`translate(${stDx},${stDy})`} {...statusGroupProps(labelInteraction)}>
              <text x={cx} y={bodyTop + bodyH * 0.46} textAnchor="middle"
                fill="#1e293b" fontSize={fs + 2} fontWeight="bold" fontFamily="monospace"
                style={isAlarm ? { animation: 'hmi-alarm 0.8s ease-in-out infinite' } : undefined}>
                {value != null ? `${val.toFixed(1)}` : '--'}
              </text>
              {unit && (
                <text x={cx} y={bodyTop + bodyH * 0.46 + fs + 4} textAnchor="middle"
                  fill="#94a3b8" fontSize={Math.max(7, fs - 1)} fontFamily="monospace">{unit}</text>
              )}

              {/* Level % */}
              <text x={cx} y={bodyTop + bodyH * 0.46 + fs + 4 + sfs + 4} textAnchor="middle"
                fill={statusColor} fontSize={sfs} fontWeight="600" fontFamily="monospace"
                style={isAlarm ? { animation: 'hmi-alarm 0.8s ease-in-out infinite' } : undefined}>
                {value != null ? `${(pct * 100).toFixed(0)}%` : ''}
              </text>
              {labelInteraction?.statusSelected && <LabelSelectionBox x={cx} y={bodyTop + bodyH * 0.46} text={value != null ? val.toFixed(1) : '--'} fontSize={fs + 2} />}
            </g>
          </>
        )}

        {/* ── Tag label (drawn last so it always stays on top of the tank body) ── */}
        <g transform={labelTransform(labelDx, labelDy, labelRotation, cx, fs + 1)} {...labelGroupProps(labelInteraction)}>
          <text x={cx} y={fs + 1} textAnchor="middle" fill={comp.labelColor || '#94a3b8'} fontSize={fs} fontWeight="700" fontFamily="monospace">{label}</text>
          {labelInteraction?.selected && <LabelSelectionBox x={cx} y={fs + 1} text={label} fontSize={fs} />}
        </g>
      </g>
    );
  }

  // ───── HORIZONTAL TANK (drum/vessel lying on its side) ───────────────────────
  if (type === 'tank-h') {
    const cy = H / 2;
    const showLevel = comp.hasLevelSensor !== false;

    // Geometry — a stadium/pill shape: a rect with fully-rounded (semicircular) ends.
    const bodyLeft  = W * 0.05;
    const bodyRight = W * 0.95;
    const bodyTop   = H * 0.18;
    const bodyBot   = H * 0.82;
    const bodyW2    = bodyRight - bodyLeft;
    const bodyH2    = bodyBot - bodyTop;
    const capR      = bodyH2 / 2;

    const fillH = bodyH2 * pct;
    const fillY = bodyBot - fillH;

    const waveCycles = Math.ceil((bodyW2 + 200) / 40) + 2;
    const waveStartX = bodyLeft - 120;
    const waveAmp    = Math.max(1.5, Math.min(5, fillH * 0.12));

    return (
      <g>
        <defs>
          {/* Shell shading runs top-to-bottom — the curved profile of a horizontal
              cylinder viewed from the side is a vertical arc, unlike the upright
              tank's left-right curve. */}
          <linearGradient id={`thb-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={shellBase} />
            <stop offset="20%"  stopColor={shellMid} />
            <stop offset="45%"  stopColor={shellLighter} />
            <stop offset="55%"  stopColor={shellLightest} stopOpacity="0.9" />
            <stop offset="80%"  stopColor={shellMid} />
            <stop offset="100%" stopColor={shellBase} />
          </linearGradient>
          <linearGradient id={`thw-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={statusColor} stopOpacity="0.80" />
            <stop offset="60%"  stopColor={statusColor} stopOpacity="0.55" />
            <stop offset="100%" stopColor={statusColor} stopOpacity="0.30" />
          </linearGradient>
          <clipPath id={`thc-${uid}`}>
            <rect x={bodyLeft} y={bodyTop} width={bodyW2} height={bodyH2} rx={capR} ry={capR} />
          </clipPath>
        </defs>

        {/* Shell body */}
        <rect x={bodyLeft} y={bodyTop} width={bodyW2} height={bodyH2} rx={capR} ry={capR}
          fill={`url(#thb-${uid})`} stroke={shellEdge} strokeWidth={1.4} />

        {/* Liquid fill */}
        {showLevel && pct > 0.01 && (
          <g clipPath={`url(#thc-${uid})`}>
            <rect x={bodyLeft} y={fillY} width={bodyW2} height={fillH} fill={`url(#thw-${uid})`} />
            {fillH > 8 && (
              <path d={waveD(waveStartX, fillY + 1, waveAmp, waveCycles, bodyBot)}
                fill={statusColor} fillOpacity={0.45}
                style={{ animation: 'hmi-water-w1 3.2s linear infinite' }} />
            )}
          </g>
        )}

        {/* End-cap highlight strips — subtle depth on the rounded ends */}
        <ellipse cx={bodyLeft + capR} cy={cy} rx={capR * 0.35} ry={bodyH2 * 0.32} fill="white" fillOpacity={0.12} />
        <ellipse cx={bodyRight - capR} cy={cy} rx={capR * 0.35} ry={bodyH2 * 0.32} fill="white" fillOpacity={0.12} />

        {/* Tag label — written on the vessel itself, like a real P&ID tag */}
        <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, cy + fs * 0.35)} {...labelGroupProps(labelInteraction)}>
          <text x={W / 2} y={cy + fs * 0.35} textAnchor="middle" fill={comp.labelColor || '#1e293b'} fontSize={fs + 1} fontWeight="700" fontFamily="monospace">{label}</text>
          {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={cy + fs * 0.35} text={label} fontSize={fs + 1} />}
        </g>

        {showLevel && (
          <>
            {/* Value + unit + level %, shown below the vessel (draggable) */}
            <g transform={`translate(${stDx},${stDy})`} {...statusGroupProps(labelInteraction)}>
              <text x={W / 2} y={H - 2} textAnchor="middle" fill={statusColor} fontSize={sfs} fontWeight="700" fontFamily="monospace"
                style={isAlarm ? { animation: 'hmi-alarm 0.8s ease-in-out infinite' } : undefined}>
                {value != null ? `${val.toFixed(1)}${unit ? ' ' + unit : ''} · ${(pct * 100).toFixed(0)}%` : ''}
              </text>
              {labelInteraction?.statusSelected && (
                <LabelSelectionBox x={W / 2} y={H - 2} text={value != null ? `${val.toFixed(1)}${unit ? ' ' + unit : ''} - ${(pct * 100).toFixed(0)}%` : ''} fontSize={sfs} />
              )}
            </g>

            {/* Alarm indicator dots along the top edge */}
            {[0, 1].map(i => {
              const ix = bodyLeft + bodyW2 * (0.30 + i * 0.40);
              const active = i === 0 ? isAlarmLo : isAlarmHi;
              const ic = i === 0 ? '#f59e0b' : '#ef4444';
              return (
                <circle key={i} cx={ix} cy={bodyTop - 5} r={3}
                  fill={active ? ic : '#e2e8f0'} stroke={ic} strokeWidth={0.8}
                  fillOpacity={active ? 0.9 : 0.25} />
              );
            })}
          </>
        )}
      </g>
    );
  }

  // ───── PIPE HORIZONTAL ───────────────────────────────────────────────────────
  if (type === 'pipe-h') {
    const cy = H / 2;
    const r  = Math.min(10, H / 2 - 2);
    return (
      <g>
        <defs>
          <linearGradient id={`ph-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={shellBase} />
            <stop offset="30%"  stopColor={shellMid} />
            <stop offset="70%"  stopColor={shellLighter} />
            <stop offset="100%" stopColor={shellBase} />
          </linearGradient>
          <clipPath id={`phc-${uid}`}>
            <rect x={0} y={cy - r} width={W} height={r * 2} rx={r} />
          </clipPath>
        </defs>

        {/* Pipe body */}
        <rect x={0} y={cy - r} width={W} height={r * 2} rx={r} fill={`url(#ph-${uid})`} />
        {/* Highlight stripe */}
        <rect x={0} y={cy - r + 2} width={W} height={3} rx={1} fill="white" fillOpacity={0.12} />

        {/* Flow animation — a dashed stroke spanning the pipe's full current length, so
            it always covers the whole pipe regardless of how long it's been resized to
            (fixed-count "flow dots" would only ever cover their original span). */}
        {isOn && (
          <g clipPath={`url(#phc-${uid})`}>
            <line x1={0} y1={cy} x2={W} y2={cy} stroke={color} strokeOpacity={flowOpacityValue} strokeWidth={r * 0.9}
              strokeLinecap="round" strokeDasharray="6 18"
              style={flowAnimStyle} />
          </g>
        )}

        {/* Pipe end caps */}
        <ellipse cx={0} cy={cy} rx={3} ry={r} fill={shellEdge} />
        <ellipse cx={W} cy={cy} rx={3} ry={r} fill={shellEdge} />

        {label && (
          <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, cy - r - 4)} {...labelGroupProps(labelInteraction)}>
            <text x={W / 2} y={cy - r - 4} textAnchor="middle" fill={comp.labelColor || '#64748b'} fontSize={fs} fontFamily="monospace">{label}</text>
            {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={cy - r - 4} text={label} fontSize={fs} />}
          </g>
        )}
      </g>
    );
  }

  // ───── PIPE VERTICAL ─────────────────────────────────────────────────────────
  if (type === 'pipe-v') {
    const cx = W / 2;
    const r  = Math.min(10, W / 2 - 2);
    return (
      <g>
        <defs>
          <linearGradient id={`pv-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor={shellBase} />
            <stop offset="30%"  stopColor={shellMid} />
            <stop offset="70%"  stopColor={shellLighter} />
            <stop offset="100%" stopColor={shellBase} />
          </linearGradient>
          <clipPath id={`pvc-${uid}`}>
            <rect x={cx - r} y={0} width={r * 2} height={H} rx={r} />
          </clipPath>
        </defs>

        <rect x={cx - r} y={0} width={r * 2} height={H} rx={r} fill={`url(#pv-${uid})`} />
        {/* Highlight stripe */}
        <rect x={cx - r + 2} y={0} width={3} height={H} rx={1} fill="white" fillOpacity={0.12} />

        {/* Flow animation — a dashed stroke spanning the pipe's full current length, so
            it always covers the whole pipe regardless of how long it's been resized to. */}
        {isOn && (
          <g clipPath={`url(#pvc-${uid})`}>
            <line x1={cx} y1={0} x2={cx} y2={H} stroke={color} strokeOpacity={flowOpacityValue} strokeWidth={r * 0.9}
              strokeLinecap="round" strokeDasharray="6 18"
              style={flowAnimStyle} />
          </g>
        )}

        <ellipse cx={cx} cy={0} rx={r} ry={3} fill={shellEdge} />
        <ellipse cx={cx} cy={H} rx={r} ry={3} fill={shellEdge} />

        {label && (
          <g transform={labelTransform(labelDx, labelDy, labelRotation, cx + r + 4, H / 2 + 4)} {...labelGroupProps(labelInteraction)}>
            <text x={cx + r + 4} y={H / 2 + 4} fill={comp.labelColor || '#64748b'} fontSize={fs} fontFamily="monospace">{label}</text>
            {labelInteraction?.selected && <LabelSelectionBox x={cx + r + 4} y={H / 2 + 4} text={label} fontSize={fs} textAnchor="start" />}
          </g>
        )}
      </g>
    );
  }

  // ───── PIPE ELBOW (curved 90° bend — connects a vertical run into a horizontal one,
  // curving out to the left or right so corners in a piping run don't need a sharp joint) ──
  if (type === 'pipe-elbow-l' || type === 'pipe-elbow-r') {
    const isRight = type === 'pipe-elbow-r';
    const cx      = W / 2;
    const midY    = H / 2;
    // Pipe half-thickness scales proportionally with the box (not just clamped at a
    // fixed max like straight pipes) so shrinking the curve thins its wall along with
    // the bend — otherwise the pipe stays "fat" next to its own shrunken curve.
    const pipeR   = Math.min(10, Math.min(W, H) * 0.15);
    // Corner radius: proportional to the box, but never smaller than the pipe's own
    // thickness (else the curve pinches tighter than the pipe and looks broken at
    // smaller sizes) and never larger than the box allows (stays inside cx/midY).
    const bendR   = Math.min(Math.max(pipeR + 3, Math.min(W, H) * 0.32), Math.min(cx, midY) - 1);
    const exitX   = isRight ? W : 0;
    const legX    = isRight ? cx + bendR : cx - bendR;

    // The centerline runs straight down from the top, through a rounded quarter-turn,
    // then straight out to the side. Using the sharp L-corner point as the quadratic
    // bezier's control point always yields a correctly-curving, tangent corner —
    // no need to reason about elliptical-arc sweep flags.
    const centerline =
      `M ${cx},0 L ${cx},${midY - bendR} ` +
      `Q ${cx},${midY} ${legX},${midY} ` +
      `L ${exitX},${midY}`;

    return (
      <g>
        <defs>
          <linearGradient id={`pe-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor={shellBase} />
            <stop offset="30%"  stopColor={shellMid} />
            <stop offset="70%"  stopColor={shellLighter} />
            <stop offset="100%" stopColor={shellBase} />
          </linearGradient>
        </defs>

        {/* Pipe body */}
        <path d={centerline} fill="none" stroke={`url(#pe-${uid})`} strokeWidth={pipeR * 2}
          strokeLinecap="round" strokeLinejoin="round" />
        {/* Sheen */}
        <path d={centerline} fill="none" stroke="white" strokeOpacity={0.10} strokeWidth={Math.max(1, pipeR * 0.5)}
          strokeLinecap="round" strokeLinejoin="round" />

        {/* Flow animation */}
        {isOn && (
          <path d={centerline} fill="none" stroke={color} strokeOpacity={flowOpacityValue} strokeWidth={pipeR * 0.9}
            strokeLinecap="round" strokeDasharray="6 18"
            style={flowAnimStyle} />
        )}

        {/* End caps */}
        <ellipse cx={cx} cy={0} rx={pipeR} ry={3} fill={shellEdge} />
        <ellipse cx={exitX} cy={midY} rx={3} ry={pipeR} fill={shellEdge} />

        {label && (
          <g transform={labelTransform(labelDx, labelDy, labelRotation, cx, H - 2)} {...labelGroupProps(labelInteraction)}>
            <text x={cx} y={H - 2} textAnchor="middle" fill={comp.labelColor || '#64748b'} fontSize={fs} fontFamily="monospace">{label}</text>
            {labelInteraction?.selected && <LabelSelectionBox x={cx} y={H - 2} text={label} fontSize={fs} />}
          </g>
        )}
      </g>
    );
  }

  // ───── VALVE ─────────────────────────────────────────────────────────────────
  // A quarter-turn ball valve: pipe stubs feed into a round body, and the lever on
  // top physically rotates — inline with the pipe when OPEN, crossways when CLOSED —
  // mirroring how a real ball/ butterfly valve handle actually works.
  if (type === 'valve') {
    const cx      = W / 2;
    const cy      = H * 0.58;
    const bodyR   = Math.min(W, H) * 0.22;
    const pipeR   = bodyR * 0.55;
    const stubLen = W * 0.20;
    const handleLen = bodyR * 1.9;
    const stemTop = cy - bodyR - 14;
    const vc      = value == null ? '#64748b' : isOn ? '#10b981' : '#ef4444';
    const vcDk    = value == null ? '#1e3a5f' : isOn ? '#064e3b' : '#7f1d1d';
    // 0° = lever inline with pipe (OPEN, flow passes through); 90° = lever crossways (CLOSED).
    const handleAngle = value == null ? 45 : isOn ? 0 : 90;

    return (
      <g>
        <defs>
          <linearGradient id={`vb-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={shellMid} />
            <stop offset="35%"  stopColor={shellLighter} />
            <stop offset="70%"  stopColor={shellMid} />
            <stop offset="100%" stopColor={shellBase} />
          </linearGradient>
          <radialGradient id={`vgl-${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor={vc} stopOpacity="0.45" />
            <stop offset="100%" stopColor={vc} stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Glow when open */}
        {isOn && (
          <circle cx={cx} cy={cy} r={bodyR + 10} fill={`url(#vgl-${uid})`}
            style={{ animation: 'hmi-glow-pulse 1.5s ease-in-out infinite' }} />
        )}

        {/* Pipe stubs feeding the valve body */}
        <rect x={cx - bodyR - stubLen} y={cy - pipeR} width={stubLen + 2} height={pipeR * 2} rx={pipeR * 0.35}
          fill={`url(#vb-${uid})`} stroke={shellEdge} strokeWidth={1} />
        <rect x={cx + bodyR - 2} y={cy - pipeR} width={stubLen + 2} height={pipeR * 2} rx={pipeR * 0.35}
          fill={`url(#vb-${uid})`} stroke={shellEdge} strokeWidth={1} />
        {/* End flanges */}
        <rect x={cx - bodyR - stubLen - 3} y={cy - pipeR - 2} width={5} height={pipeR * 2 + 4} rx={1} fill={shellMid} stroke={shellEdge} strokeWidth={0.8} />
        <rect x={cx + bodyR + stubLen - 2} y={cy - pipeR - 2} width={5} height={pipeR * 2 + 4} rx={1} fill={shellMid} stroke={shellEdge} strokeWidth={0.8} />

        {/* Valve body (round casing) — stays the housing color; only the handle below
            indicates OPEN/CLOSED, so the whole valve doesn't flash red/green */}
        <circle cx={cx} cy={cy} r={bodyR} fill={`url(#vb-${uid})`} stroke={shellEdge} strokeWidth={1.6} />
        <ellipse cx={cx - bodyR * 0.3} cy={cy - bodyR * 0.35} rx={bodyR * 0.35} ry={bodyR * 0.22}
          fill="white" fillOpacity={0.10} />
        {/* Flow port glowing through the body when open */}
        {isOn && <circle cx={cx} cy={cy} r={bodyR * 0.3} fill={vc} fillOpacity={0.85} />}

        {/* Bonnet / stem riser up to the handle pivot */}
        <rect x={cx - 3} y={stemTop} width={6} height={cy - bodyR - stemTop} rx={2} fill={shellMid} stroke={shellEdge} strokeWidth={1} />

        {/* Handle / lever — rotates about the stem to show OPEN vs CLOSED */}
        <g style={{ transformOrigin: `${cx}px ${stemTop + 4}px`, transform: `rotate(${handleAngle}deg)`, transition: 'transform 0.4s ease' }}>
          <rect x={cx - handleLen} y={stemTop} width={handleLen * 2} height={7} rx={3.5}
            fill={vcDk} stroke={vc} strokeWidth={1.2} />
          <circle cx={cx} cy={stemTop + 3.5} r={3} fill={vc} />
        </g>

        {/* Label + status at bottom */}
        {label && (
          <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, H - 13)} {...labelGroupProps(labelInteraction)}>
            <text x={W / 2} y={H - 13} textAnchor="middle" fill={comp.labelColor || '#334155'} fontSize={fs} fontWeight="700" fontFamily="monospace">{label}</text>
            {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={H - 13} text={label} fontSize={fs} />}
          </g>
        )}
        <g transform={`translate(${stDx},${stDy})`} {...statusGroupProps(labelInteraction)}>
          <text x={W / 2} y={H - 2} textAnchor="middle" fill={vc} fontSize={sfs} fontWeight="700" fontFamily="monospace"
            style={isAlarm ? { animation: 'hmi-alarm 0.8s ease-in-out infinite' } : undefined}>
            {value == null ? '' : isOn ? 'OPEN' : 'CLOSED'}
          </text>
          {labelInteraction?.statusSelected && <LabelSelectionBox x={W / 2} y={H - 2} text={value == null ? '' : isOn ? 'OPEN' : 'CLOSED'} fontSize={sfs} />}
        </g>
      </g>
    );
  }

  // ───── FLOW CONTROL VALVE (0-100% modulating, cutaway-illustration style) ────
  // A labeled control-valve cutaway: inlet wheel -> valve body with an internal
  // plug and flow arrows -> stem -> spring-and-diaphragm actuator bonnet ->
  // downstream block -> outlet arrow. The plug physically lifts off its seat as
  // the position rises from 0-100%, and the spring visibly compresses to match —
  // both colored along a red (closed) -> green (open) blend so the percentage
  // reads at a glance, layered onto the illustrative shapes/colors themselves.
  if (type === 'valve-fcv') {
    const cy        = H * 0.62;
    // Accent Color picks the "fully open" (100%) end of the sweep — closed (0%)
    // always stays red, so the color still reads as a status at a glance, but the
    // open color is now actually customizable instead of being hardcoded green.
    const openColor  = comp.color || '#10b981';
    const openDark   = blendColor(openColor, '#000000', 0.35);
    const posColor   = value == null ? '#64748b' : blendColor('#ef4444', openColor, pct);
    const posDark    = value == null ? shellBase : blendColor('#991b1b', openDark, pct);

    // Inlet wheel
    const wheelCx = W * 0.11, wheelR = Math.min(H * 0.22, W * 0.11);

    // Valve body
    const bodyLeft = W * 0.25, bodyRight = W * 0.56;
    const bodyTop  = cy - H * 0.17, bodyBot = cy + H * 0.17;
    const plugCx   = (bodyLeft + bodyRight) / 2;
    const plugW    = (bodyRight - bodyLeft) * 0.20;
    const gapH     = (bodyBot - bodyTop - 6) * pct; // grows as the valve opens
    const plugTop  = bodyTop + 3;
    const plugBot  = bodyBot - 3 - gapH;
    const plugH    = Math.max(2, plugBot - plugTop);
    const flowOpacity = value == null ? 0.25 : 0.3 + pct * 0.65;

    // Stem + actuator bonnet
    const stemTopY = H * 0.24;
    const bonnetRx = W * 0.13;
    const bonnetTopY = H * 0.05;
    // The spring visibly compresses as the valve opens — fewer, tighter coils at 100%.
    const springTop = bonnetTopY + 9;
    const springBot = stemTopY - 4 - pct * (stemTopY - bonnetTopY - 13) * 0.4;
    const springCoils = 4;

    // Downstream block
    const blockLeft = bodyRight + W * 0.05, blockRight = W * 0.86;
    const blockTop = cy - H * 0.14, blockBot = cy + H * 0.14;

    return (
      <g>
        <defs>
          <radialGradient id={`fcvw-${uid}`} cx="35%" cy="30%" r="70%">
            <stop offset="0%"   stopColor={shellLighter} />
            <stop offset="100%" stopColor={shellMid} />
          </radialGradient>
          <linearGradient id={`fcvbn-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#e8c9a0" />
            <stop offset="100%" stopColor="#c9a06e" />
          </linearGradient>
          <linearGradient id={`fcvblk-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={shellLighter} />
            <stop offset="100%" stopColor={shellMid} />
          </linearGradient>
        </defs>

        {/* Leading inlet arrow */}
        <polygon points={`${wheelCx - wheelR - W * 0.10},${cy - H * 0.07} ${wheelCx - wheelR - W * 0.10},${cy + H * 0.07} ${wheelCx - wheelR + 2},${cy}`} fill="#3b82f6" />

        {/* Inlet wheel */}
        <circle cx={wheelCx} cy={cy} r={wheelR} fill={`url(#fcvw-${uid})`} stroke={shellEdge} strokeWidth={1.6} />
        <circle cx={wheelCx} cy={cy} r={wheelR * 0.32} fill={shellBase} stroke={shellEdge} strokeWidth={1} />
        {[0, 60, 120, 180, 240, 300].map(a => {
          const rad = (a * Math.PI) / 180;
          return <line key={a} x1={wheelCx} y1={cy} x2={wheelCx + Math.cos(rad) * wheelR * 0.85} y2={cy + Math.sin(rad) * wheelR * 0.85} stroke={shellEdge} strokeWidth={1} strokeOpacity={0.5} />;
        })}

        {/* Valve body (light blue-gray casing, like the reference illustration) */}
        <rect x={bodyLeft} y={bodyTop} width={bodyRight - bodyLeft} height={bodyBot - bodyTop} rx={4}
          fill="#bcd6e6" stroke={shellEdge} strokeWidth={1.6} />
        <rect x={bodyLeft + 2} y={bodyTop + 2} width={bodyRight - bodyLeft - 4} height={Math.min(5, (bodyBot - bodyTop) * 0.18)} rx={2} fill="white" fillOpacity={0.35} />

        {/* Inlet flow arrow (blue) inside the body */}
        <polygon points={`${bodyLeft + 4},${cy - 5} ${bodyLeft + 4},${cy + 5} ${plugCx - plugW / 2 - 2},${cy}`} fill="#2563eb" fillOpacity={flowOpacity} />
        {/* Outlet flow arrow (red) inside the body */}
        <polygon points={`${plugCx + plugW / 2 + 2},${cy - 5} ${plugCx + plugW / 2 + 2},${cy + 5} ${bodyRight - 4},${cy}`} fill="#ef4444" fillOpacity={flowOpacity} />

        {/* The plug — physically lifts off its seat as position rises from 0-100% */}
        <rect x={plugCx - plugW / 2} y={plugTop} width={plugW} height={plugH} rx={1.5}
          fill={posColor} stroke={posDark} strokeWidth={1.2}
          style={{ transition: 'height 0.4s ease, fill 0.4s ease' }} />

        {/* Stem from the valve body up into the actuator */}
        <rect x={plugCx - 2} y={stemTopY} width={4} height={bodyTop - stemTopY} fill={shellMid} stroke={shellEdge} strokeWidth={0.8} />
        {/* Yoke bracket around the stem, like the reference's frame */}
        <rect x={plugCx - bonnetRx * 0.7} y={stemTopY - 2} width={bonnetRx * 1.4} height={4} fill={shellMid} stroke={shellEdge} strokeWidth={0.8} />

        {/* Spring-and-diaphragm actuator bonnet */}
        <path
          d={`M ${plugCx - bonnetRx},${stemTopY} L ${plugCx - bonnetRx},${bonnetTopY + bonnetRx * 0.5}
              Q ${plugCx - bonnetRx},${bonnetTopY} ${plugCx},${bonnetTopY}
              Q ${plugCx + bonnetRx},${bonnetTopY} ${plugCx + bonnetRx},${bonnetTopY + bonnetRx * 0.5}
              L ${plugCx + bonnetRx},${stemTopY} Z`}
          fill={`url(#fcvbn-${uid})`} stroke={shellEdge} strokeWidth={1.4}
        />
        {/* Spring coils inside the bonnet — visibly compress as % rises */}
        {Array.from({ length: springCoils }, (_, i) => {
          const t0 = i / springCoils, t1 = (i + 0.5) / springCoils, t2 = (i + 1) / springCoils;
          const y0 = springTop + (springBot - springTop) * t0;
          const y1 = springTop + (springBot - springTop) * t1;
          const y2 = springTop + (springBot - springTop) * t2;
          return (
            <path key={i}
              d={`M ${plugCx - bonnetRx * 0.4},${y0} L ${plugCx + bonnetRx * 0.4},${y1} L ${plugCx - bonnetRx * 0.4},${y2}`}
              fill="none" stroke={shellEdge} strokeWidth={1.3} strokeOpacity={0.75}
              style={{ transition: 'd 0.4s ease' }}
            />
          );
        })}
        {/* Thumbscrew / bolt on top */}
        <rect x={plugCx - 4} y={bonnetTopY - 5} width={8} height={6} rx={1} fill={shellMid} stroke={shellEdge} strokeWidth={0.8} />
        <line x1={plugCx - 2.5} y1={bonnetTopY - 2} x2={plugCx + 2.5} y2={bonnetTopY - 2} stroke={shellEdge} strokeWidth={0.8} />

        {/* Downstream connection block */}
        <rect x={blockLeft} y={blockTop} width={blockRight - blockLeft} height={blockBot - blockTop} rx={3}
          fill={`url(#fcvblk-${uid})`} stroke={shellEdge} strokeWidth={1.4} />
        <rect x={blockLeft + 2} y={blockTop + 2} width={blockRight - blockLeft - 4} height={Math.min(4, (blockBot - blockTop) * 0.18)} rx={2} fill="white" fillOpacity={0.3} />

        {/* Trailing outlet arrow */}
        <polygon points={`${blockRight + 2},${cy - H * 0.07} ${blockRight + 2},${cy + H * 0.07} ${Math.min(W - 2, blockRight + W * 0.10)},${cy}`} fill="#ef4444" fillOpacity={flowOpacity} />

        {/* Label */}
        {label && (
          <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, H - 13)} {...labelGroupProps(labelInteraction)}>
            <text x={W / 2} y={H - 13} textAnchor="middle" fill={comp.labelColor || '#94a3b8'} fontSize={fs} fontWeight="700" fontFamily="monospace">{label}</text>
            {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={H - 13} text={label} fontSize={fs} />}
          </g>
        )}

        {/* Position % */}
        <g transform={`translate(${stDx},${stDy})`} {...statusGroupProps(labelInteraction)}>
          <text x={W / 2} y={H - 2} textAnchor="middle" fill={posColor} fontSize={sfs} fontWeight="700" fontFamily="monospace"
            style={isAlarm ? { animation: 'hmi-alarm 0.8s ease-in-out infinite' } : undefined}>
            {value != null ? `${(pct * 100).toFixed(0)}%` : ''}
          </text>
          {labelInteraction?.statusSelected && <LabelSelectionBox x={W / 2} y={H - 2} text={value != null ? `${(pct * 100).toFixed(0)}%` : ''} fontSize={sfs} />}
        </g>
      </g>
    );
  }

  // ───── PUMP ──────────────────────────────────────────────────────────────────
  // Volute housing is solid status-colored — green while running, red while
  // stopped (gray with no live data) — with white impeller blades for contrast,
  // matching the same on/off color convention as the pump-style motor.
  if (type === 'pump') {
    const cx = W / 2;
    const cy = H * 0.47;
    const R  = Math.min(W, H * 0.85) / 2 - 8;
    const statusFill = value == null ? '#64748b' : isOn ? '#10b981' : '#ef4444';
    const statusDark = value == null ? shellBase : isOn ? '#047857' : '#991b1b';
    const lc = value == null ? '#64748b' : isOn ? '#10b981' : '#ef4444';

    return (
      <g>
        <defs>
          <radialGradient id={`pgr-${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor={statusFill} stopOpacity="0.5" />
            <stop offset="100%" stopColor={statusFill} stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Glow ring when running */}
        {isOn && (
          <circle cx={cx} cy={cy} r={R + 8} fill={`url(#pgr-${uid})`}
            style={{ animation: 'hmi-glow-pulse 1.2s ease-in-out infinite' }} />
        )}

        {/* Housing ring (outer metal rim) */}
        <circle cx={cx} cy={cy} r={R + 4} fill={shellMid} stroke={shellEdge} strokeWidth={2} />
        {/* Volute housing — solid status color */}
        <circle cx={cx} cy={cy} r={R} fill={statusFill} stroke={statusDark} strokeWidth={1.4} />
        {/* Housing shine */}
        <ellipse cx={cx - R * 0.32} cy={cy - R * 0.32} rx={R * 0.32} ry={R * 0.20} fill="white" fillOpacity={0.22} />

        {/* Inlet nozzle (left) */}
        <rect x={bX(cx, R, -1) - 10} y={cy - 6} width={14} height={12} rx={2} fill={shellMid} stroke={shellEdge} strokeWidth={1.2} />
        {/* Outlet nozzle (top) */}
        <rect x={cx - 6} y={cy - R - 4 - 12} width={12} height={14} rx={2} fill={shellMid} stroke={shellEdge} strokeWidth={1.2} />

        {/* Spinning impeller */}
        <g style={{ transformOrigin: `${cx}px ${cy}px`, animation: isOn ? 'hmi-spin 1.2s linear infinite' : 'none' }}>
          {/* 4 curved blades */}
          {[0, 90, 180, 270].map(angle => {
            const rad = (angle * Math.PI) / 180;
            const x1 = cx + Math.cos(rad) * 5;
            const y1 = cy + Math.sin(rad) * 5;
            const x2 = cx + Math.cos(rad + 0.6) * (R - 8);
            const y2 = cy + Math.sin(rad + 0.6) * (R - 8);
            return (
              <path
                key={angle}
                d={`M${x1},${y1} Q${cx + Math.cos(rad + 0.3) * (R - 14)},${cy + Math.sin(rad + 0.3) * (R - 14)} ${x2},${y2}`}
                stroke="white" strokeOpacity={0.85} strokeWidth={3} fill="none" strokeLinecap="round"
              />
            );
          })}
          {/* Hub */}
          <circle cx={cx} cy={cy} r={5} fill="white" fillOpacity={0.85} />
        </g>

        {/* Status */}
        <g transform={`translate(${stDx},${stDy})`} {...statusGroupProps(labelInteraction)}>
          <text x={W / 2} y={H - 11} textAnchor="middle" fill={lc} fontSize={sfs} fontWeight="700" fontFamily="monospace">
            {value == null ? '' : isOn ? 'RUNNING' : 'STOPPED'}
          </text>
          {labelInteraction?.statusSelected && <LabelSelectionBox x={W / 2} y={H - 11} text={value == null ? '' : isOn ? 'RUNNING' : 'STOPPED'} fontSize={sfs} />}
        </g>
        <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, H - 1)} {...labelGroupProps(labelInteraction)}>
          <text x={W / 2} y={H - 1} textAnchor="middle" fill={comp.labelColor || '#94a3b8'} fontSize={fs} fontWeight="600" fontFamily="monospace">{label}</text>
          {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={H - 1} text={label} fontSize={fs} />}
        </g>
      </g>
    );
  }

  // ───── MOTOR ─────────────────────────────────────────────────────────────────
  // Uses a real motor/pump photo instead of a drawn icon — status (running glow,
  // spinning shaft ring, RUNNING/STOPPED text) is overlaid on top of the photo.
  if (type === 'motor') {
    const cx = W / 2;
    const bW = W - 12, bH = H - 30, bX2 = 6, bY2 = 14;
    const lc = value == null ? '#64748b' : isOn ? '#10b981' : '#6b7280';

    return (
      <g>
        {/* Running glow */}
        {isOn && (
          <rect x={bX2 - 4} y={bY2 - 4} width={bW + 8} height={bH + 8} rx={7} fill={color} fillOpacity={0.12}
            style={{ animation: 'hmi-glow-pulse 1.5s ease-in-out infinite' }} />
        )}

        {/* Realistic motor photo */}
        <image href="/img/motor.png" x={bX2} y={bY2} width={bW} height={bH} preserveAspectRatio="xMidYMid meet" />

        {/* Rotation indicator ring — shows the shaft turning while running */}
        <g style={{ transformOrigin: `${cx}px ${bY2 + bH / 2}px`, animation: isOn ? 'hmi-spin 2s linear infinite' : 'none' }}>
          <circle cx={cx} cy={bY2 + bH / 2} r={bH * 0.28} fill="none" stroke={isOn ? color : shellBase} strokeWidth={1.5} strokeDasharray="4 6" strokeOpacity={0.6} />
        </g>

        {/* Status */}
        <g transform={`translate(${stDx},${stDy})`} {...statusGroupProps(labelInteraction)}>
          <text x={W / 2} y={H - 1} textAnchor="middle" fill={lc} fontSize={sfs} fontWeight="700" fontFamily="monospace">
            {value == null ? '' : isOn ? 'RUNNING' : 'STOPPED'}
          </text>
          {labelInteraction?.statusSelected && <LabelSelectionBox x={W / 2} y={H - 1} text={value == null ? '' : isOn ? 'RUNNING' : 'STOPPED'} fontSize={sfs} />}
        </g>

        {/* Label (drawn last so it always stays on top of the photo) */}
        <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, fs + 1)} {...labelGroupProps(labelInteraction)}>
          <text x={W / 2} y={fs + 1} textAnchor="middle" fill={comp.labelColor || '#94a3b8'} fontSize={fs} fontWeight="700" fontFamily="monospace">{label}</text>
          {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={fs + 1} text={label} fontSize={fs} />}
        </g>
      </g>
    );
  }

  // ───── MOTOR (PUMP-STYLE, COLOR STATUS) ───────────────────────────────────────
  // Same silhouette as the reference photo (centrifugal pump + finned motor,
  // inlet arrow) — but drawn as a vector so the whole body can flip color:
  // solid green while running, solid red while stopped.
  if (type === 'motor-industrial') {
    const cy       = H * 0.56;
    const statusFill = value == null ? '#64748b' : isOn ? '#10b981' : '#ef4444';
    const statusDark = value == null ? shellBase : isOn ? '#047857' : '#991b1b';
    const lc       = value == null ? '#64748b' : isOn ? '#10b981' : '#ef4444';

    // Inlet flow arrow (small, fixed accent colour — not part of the run/stop status)
    const arrowTipX = W * 0.15, arrowBaseX = W * 0.02, arrowHalfH = H * 0.09;

    // Pump volute (rounded housing on the left)
    const pumpCx = W * 0.30, pumpCy = cy, pumpR = H * 0.30;

    // Motor cylinder (finned housing on the right)
    const motorX = pumpCx + pumpR + W * 0.04;
    const motorW = W * 0.50;
    const motorH = H * 0.58;
    const motorY = cy - motorH / 2;
    const capW   = motorW * 0.14;
    const finCount = 6;
    const capCx  = motorX + motorW - capW / 2;
    const fanR   = Math.min(capW, motorH) * 0.38;

    return (
      <g>
        <defs>
          <radialGradient id={`mps-pg-${uid}`} cx="38%" cy="32%" r="70%">
            <stop offset="0%"  stopColor={statusFill} stopOpacity="0.55" />
            <stop offset="100%" stopColor={statusFill} stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Running glow */}
        {isOn && (
          <ellipse cx={(pumpCx + motorX + motorW) / 2} cy={cy} rx={W * 0.42} ry={H * 0.5} fill={`url(#mps-pg-${uid})`}
            style={{ animation: 'hmi-glow-pulse 1.5s ease-in-out infinite' }} />
        )}

        {/* Base / feet */}
        <rect x={pumpCx - pumpR * 0.7} y={cy + Math.max(pumpR, motorH / 2) + 2} width={motorX + motorW - capW - (pumpCx - pumpR * 0.7)} height={3.5} rx={1.5} fill={shellEdge} fillOpacity={0.7} />

        {/* Inlet flow arrow */}
        <polygon points={`${arrowBaseX},${cy - arrowHalfH} ${arrowBaseX},${cy + arrowHalfH} ${arrowTipX},${cy}`} fill="#3b82f6" />

        {/* Pump volute */}
        <circle cx={pumpCx} cy={pumpCy} r={pumpR} fill={statusFill} stroke={statusDark} strokeWidth={1.6} />
        {/* Discharge nozzle on top of the pump */}
        <rect x={pumpCx - pumpR * 0.20} y={pumpCy - pumpR - 6} width={pumpR * 0.40} height={8} rx={1.5} fill={statusFill} stroke={statusDark} strokeWidth={1.2} />
        {/* Pump shine */}
        <ellipse cx={pumpCx - pumpR * 0.32} cy={pumpCy - pumpR * 0.35} rx={pumpR * 0.34} ry={pumpR * 0.22} fill="white" fillOpacity={0.22} />

        {/* Coupling between pump and motor */}
        <rect x={pumpCx + pumpR - 2} y={cy - H * 0.07} width={motorX - (pumpCx + pumpR) + 4} height={H * 0.14} fill={shellMid} stroke={shellEdge} strokeWidth={1} />

        {/* Motor cylinder */}
        <rect x={motorX} y={motorY} width={motorW} height={motorH} rx={3} fill={statusFill} stroke={statusDark} strokeWidth={1.6} />
        {/* Cooling fin ridges */}
        {Array.from({ length: finCount }, (_, i) => {
          const fx = motorX + motorW * 0.10 + i * (motorW * 0.62) / (finCount - 1);
          return <rect key={i} x={fx} y={motorY + 1} width={Math.max(2, motorW * 0.035)} height={motorH - 2} fill={statusDark} fillOpacity={0.35} />;
        })}
        {/* Motor shine */}
        <rect x={motorX + 2} y={motorY + 2} width={motorW - capW - 4} height={Math.min(5, motorH * 0.2)} rx={2} fill="white" fillOpacity={0.22} />
        {/* Dark end cap (fan cover) */}
        <rect x={motorX + motorW - capW} y={motorY} width={capW} height={motorH} rx={2} fill={shellEdge} />

        {/* Spinning cooling fan, visible through the end cap — the one part of a real
            motor that visibly rotates from the outside */}
        <g style={{ transformOrigin: `${capCx}px ${cy}px`, animation: isOn ? 'hmi-spin 0.5s linear infinite' : 'none' }}>
          {[0, 120, 240].map(angle => {
            const rad = (angle * Math.PI) / 180;
            const x2 = capCx + Math.cos(rad) * fanR;
            const y2 = cy + Math.sin(rad) * fanR;
            return (
              <line key={angle} x1={capCx} y1={cy} x2={x2} y2={y2}
                stroke={statusFill} strokeWidth={Math.max(1.5, fanR * 0.35)} strokeLinecap="round" strokeOpacity={0.9} />
            );
          })}
          <circle cx={capCx} cy={cy} r={Math.max(1.5, fanR * 0.28)} fill={statusFill} />
        </g>

        {/* Status */}
        <g transform={`translate(${stDx},${stDy})`} {...statusGroupProps(labelInteraction)}>
          <text x={W / 2} y={H - 1} textAnchor="middle" fill={lc} fontSize={sfs} fontWeight="700" fontFamily="monospace">
            {value == null ? '' : isOn ? 'RUNNING' : 'STOPPED'}
          </text>
          {labelInteraction?.statusSelected && <LabelSelectionBox x={W / 2} y={H - 1} text={value == null ? '' : isOn ? 'RUNNING' : 'STOPPED'} fontSize={sfs} />}
        </g>

        {/* Label */}
        <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, fs + 1)} {...labelGroupProps(labelInteraction)}>
          <text x={W / 2} y={fs + 1} textAnchor="middle" fill={comp.labelColor || '#94a3b8'} fontSize={fs} fontWeight="700" fontFamily="monospace">{label}</text>
          {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={fs + 1} text={label} fontSize={fs} />}
        </g>
      </g>
    );
  }

  // ───── AGITATOR (MIXER) ────────────────────────────────────────────────────
  // Gear motor + shaft + impeller, meant to sit on top of a Tank/Tank-H widget
  // in the layout (drop a Tank first, then an Agitator over its lid) — same
  // "just the mechanical unit, not the vessel" convention as Motor/Pump.
  if (type === 'agitator') {
    const cx = W / 2;
    const motorW = W * 0.56, motorH = H * 0.16;
    const motorX = cx - motorW / 2, motorY = 2;
    const capW = motorW * 0.18;
    const finCount = 4;
    const statusFill = value == null ? '#64748b' : isOn ? '#10b981' : '#ef4444';
    const statusDark = value == null ? shellBase : isOn ? '#047857' : '#991b1b';
    const lc = value == null ? '#64748b' : isOn ? '#10b981' : '#ef4444';

    const flangeY = motorY + motorH + 2;
    const flangeW = motorW * 0.62;
    const shaftTopY = flangeY + 5;
    const bladeCy = H - sfs - 10; // leave room for the status readout below
    const shaftW = Math.max(3, W * 0.045);

    // Impeller — three real paddle blades (not flattened lines) so a rigid
    // CSS rotation reads as a clean spin instead of a distorted wobble.
    const bladeCount = 3;
    const bladeLen = W * 0.36;
    const baseHalfW = Math.max(3, H * 0.026);
    const tipHalfW = Math.max(1.5, baseHalfW * 0.4);
    const hubR = Math.max(3.5, baseHalfW * 1.3);
    const bladeFill = value == null ? '#64748b' : isOn ? color : shellMid;
    const bladePoints = [
      [cx + hubR * 0.6, bladeCy - baseHalfW],
      [cx + bladeLen, bladeCy - tipHalfW],
      [cx + bladeLen, bladeCy + tipHalfW],
      [cx + hubR * 0.6, bladeCy + baseHalfW],
    ].map(([x, y]) => `${x},${y}`).join(' ');

    // Spin duration scales down as the blade gets bigger so tip speed reads
    // consistently across different component sizes instead of a huge
    // impeller looking sluggish or a tiny one looking frantic.
    const spinDurationS = Math.max(0.5, Math.min(1.4, bladeLen / 45));

    return (
      <g>
        {/* Running glow around the motor */}
        {isOn && (
          <ellipse cx={cx} cy={motorY + motorH / 2} rx={motorW * 0.9} ry={motorH * 1.4} fill={statusFill} fillOpacity={0.15}
            style={{ animation: 'hmi-glow-pulse 1.5s ease-in-out infinite' }} />
        )}

        {/* Gear motor housing */}
        <rect x={motorX} y={motorY} width={motorW} height={motorH} rx={3} fill={statusFill} stroke={statusDark} strokeWidth={1.6} />
        {Array.from({ length: finCount }, (_, i) => {
          const fx = motorX + motorW * 0.12 + i * (motorW * 0.62) / (finCount - 1);
          return <rect key={i} x={fx} y={motorY + 1} width={Math.max(1.5, motorW * 0.03)} height={motorH - 2} fill={statusDark} fillOpacity={0.35} />;
        })}
        <rect x={motorX + 1.5} y={motorY + 1.5} width={motorW - capW - 3} height={Math.min(4, motorH * 0.3)} rx={1.5} fill="white" fillOpacity={0.22} />
        {/* End cap */}
        <rect x={motorX + motorW - capW} y={motorY} width={capW} height={motorH} rx={2} fill={shellEdge} />

        {/* Mounting flange onto the tank lid */}
        <rect x={cx - flangeW / 2} y={flangeY} width={flangeW} height={5} rx={1.5} fill={shellMid} stroke={shellEdge} strokeWidth={1} />
        <circle cx={cx - flangeW / 2 + 4} cy={flangeY + 2.5} r={1.3} fill={shellEdge} />
        <circle cx={cx + flangeW / 2 - 4} cy={flangeY + 2.5} r={1.3} fill={shellEdge} />

        {/* Shaft */}
        <rect x={cx - shaftW / 2} y={shaftTopY} width={shaftW} height={Math.max(0, bladeCy - shaftTopY)} fill={shellBase} stroke={shellEdge} strokeWidth={0.75} />
        <rect x={cx - shaftW / 2} y={shaftTopY} width={Math.max(1, shaftW * 0.35)} height={Math.max(0, bladeCy - shaftTopY)} fill="white" fillOpacity={0.18} />

        {/* Mixing ripple — a soft pulsing ring around the blades while running,
            reading as fluid disturbance instead of the impeller spinning in air */}
        {isOn && (
          <ellipse cx={cx} cy={bladeCy} rx={bladeLen * 1.15} ry={bladeLen * 0.42} fill="none" stroke={color} strokeWidth={1.2} strokeOpacity={0.35}
            style={{ animation: 'hmi-glow-pulse 1.1s ease-in-out infinite' }} />
        )}

        {/* Impeller — spins around the shaft's bottom end while running */}
        <g style={{ transformOrigin: `${cx}px ${bladeCy}px`, animation: isOn ? `hmi-spin ${spinDurationS}s linear infinite` : 'none' }}>
          {Array.from({ length: bladeCount }, (_, i) => (
            <polygon
              key={i}
              points={bladePoints}
              fill={bladeFill}
              fillOpacity={0.95}
              stroke={shellEdge}
              strokeWidth={0.75}
              strokeLinejoin="round"
              transform={`rotate(${(360 / bladeCount) * i}, ${cx}, ${bladeCy})`}
            />
          ))}
          <circle cx={cx} cy={bladeCy} r={hubR} fill={shellEdge} stroke={shellBase} strokeWidth={0.75} />
        </g>

        {/* Status */}
        <g transform={`translate(${stDx},${stDy})`} {...statusGroupProps(labelInteraction)}>
          <text x={W / 2} y={H - 1} textAnchor="middle" fill={lc} fontSize={sfs} fontWeight="700" fontFamily="monospace">
            {value == null ? '' : isOn ? 'RUNNING' : 'STOPPED'}
          </text>
          {labelInteraction?.statusSelected && <LabelSelectionBox x={W / 2} y={H - 1} text={value == null ? '' : isOn ? 'RUNNING' : 'STOPPED'} fontSize={sfs} />}
        </g>

        {/* Label */}
        <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, fs + 1)} {...labelGroupProps(labelInteraction)}>
          <text x={W / 2} y={fs + 1} textAnchor="middle" fill={comp.labelColor || '#94a3b8'} fontSize={fs} fontWeight="700" fontFamily="monospace">{label}</text>
          {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={fs + 1} text={label} fontSize={fs} />}
        </g>
      </g>
    );
  }

  // ───── SENSOR ────────────────────────────────────────────────────────────────
  // A small solid-colored value chip (status/alarm color) with the reading inside
  // and the tag name sitting below it — like a simple PV/readout tile.
  if (type === 'sensor') {
    return (
      <g>
        {/* Alarm glow */}
        {isAlarm && (
          <rect x={-4} y={-4} width={W + 8} height={H + 8} rx={Math.min(10, H * 0.2) + 4} fill={statusColor} fillOpacity={0.25}
            style={{ animation: 'hmi-alarm 0.6s ease-in-out infinite' }} />
        )}

        {/* Solid status-colored chip */}
        <rect x={0} y={0} width={W} height={H} rx={Math.min(8, H * 0.2)} fill={statusColor} stroke={shellEdge} strokeWidth={1} />
        {/* Subtle top sheen for a touch of depth */}
        <rect x={1} y={1} width={W - 2} height={Math.min(8, H * 0.35)} rx={3} fill="white" fillOpacity={0.15} />

        {/* Value (+ unit), centered, white for contrast against the solid chip */}
        <text x={W / 2} y={H / 2 + fs * 0.36} textAnchor="middle" fill="white" fontSize={Math.min(fs + 1, H * 0.5)} fontWeight="bold" fontFamily="monospace"
          style={isAlarm ? { animation: 'hmi-alarm 0.8s ease-in-out infinite' } : undefined}>
          {value != null ? val.toFixed(2) : '--'}{unit ? ` ${unit}` : ''}
        </text>

        {/* Tag label — outside, below the chip (drawn last so it's always draggable/on top) */}
        <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, H + fs)} {...labelGroupProps(labelInteraction)}>
          <text x={W / 2} y={H + fs} textAnchor="middle" fill={comp.labelColor || '#64748b'} fontSize={fs} fontWeight="600" fontFamily="monospace">{label}</text>
          {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={H + fs} text={label} fontSize={fs} />}
        </g>
      </g>
    );
  }

  // ───── LINE ──────────────────────────────────────────────────────────────────
  // A plain straight line spanning its bounding box — rotate it (45° steps, in the
  // Rotation panel) to angle it vertically/diagonally instead of having separate
  // horizontal/vertical variants like the pipes do.
  if (type === 'line') {
    const cy = H / 2;
    const thickness = comp.lineThickness ?? 3;
    return (
      <g>
        <line x1={0} y1={cy} x2={W} y2={cy} stroke={color} strokeWidth={thickness} strokeLinecap="round"
          strokeDasharray={lineDashArray(comp.lineStyle, thickness)} />
        {label && (
          <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, cy - thickness / 2 - 4)} {...labelGroupProps(labelInteraction)}>
            <text x={W / 2} y={cy - thickness / 2 - 4} textAnchor="middle" fill={comp.labelColor || '#64748b'} fontSize={fs} fontFamily="monospace">{label}</text>
            {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={cy - thickness / 2 - 4} text={label} fontSize={fs} />}
          </g>
        )}
      </g>
    );
  }

  // ───── IMAGE ─────────────────────────────────────────────────────────────────
  // A user-uploaded picture (logo, floor plan, equipment photo, …) placed on the
  // canvas like any other widget — resizable, rotatable, draggable.
  if (type === 'image') {
    const preserveAspect = comp.imageFit === 'stretch' ? 'none' : 'xMidYMid meet';
    return (
      <g>
        {comp.imageUrl ? (
          <image href={comp.imageUrl} x={0} y={0} width={W} height={H} preserveAspectRatio={preserveAspect} />
        ) : (
          <>
            <rect x={0} y={0} width={W} height={H} rx={4} fill="none" stroke="#475569" strokeWidth={1.5} strokeDasharray="6 4" />
            <text x={W / 2} y={H / 2} textAnchor="middle" fill="#64748b" fontSize={11} fontFamily="monospace">No image</text>
          </>
        )}
        {label && (
          <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, H + fs)} {...labelGroupProps(labelInteraction)}>
            <text x={W / 2} y={H + fs} textAnchor="middle" fill={comp.labelColor || '#64748b'} fontSize={fs} fontFamily="monospace">{label}</text>
            {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={H + fs} text={label} fontSize={fs} />}
          </g>
        )}
      </g>
    );
  }

  // ───── ARROW ─────────────────────────────────────────────────────────────────
  // A straight shaft with a filled triangular head at its end — like Line, rotate
  // it (45° steps) to point any direction instead of separate variants per angle.
  if (type === 'arrow') {
    const cy = H / 2;
    const thickness = comp.lineThickness ?? 3;
    const headLen  = Math.min(W * 0.4, Math.max(10, thickness * 4));
    const headHalf = headLen * 0.6;
    const shaftEnd = Math.max(0, W - headLen);
    return (
      <g>
        <line x1={0} y1={cy} x2={shaftEnd} y2={cy} stroke={color} strokeWidth={thickness} strokeLinecap="round"
          strokeDasharray={lineDashArray(comp.lineStyle, thickness)} />
        <polygon points={`${W},${cy} ${shaftEnd},${cy - headHalf} ${shaftEnd},${cy + headHalf}`} fill={color} />
        {label && (
          <g transform={labelTransform(labelDx, labelDy, labelRotation, W / 2, cy - headHalf - 4)} {...labelGroupProps(labelInteraction)}>
            <text x={W / 2} y={cy - headHalf - 4} textAnchor="middle" fill={comp.labelColor || '#64748b'} fontSize={fs} fontFamily="monospace">{label}</text>
            {labelInteraction?.selected && <LabelSelectionBox x={W / 2} y={cy - headHalf - 4} text={label} fontSize={fs} />}
          </g>
        )}
      </g>
    );
  }

  // ───── LABEL ─────────────────────────────────────────────────────────────────
  if (type === 'label') {
    return (
      <text
        x={4}
        y={H / 2 + (comp.fontSize || 14) * 0.38}
        fill={comp.labelColor || color}
        fontSize={comp.fontSize || 14}
        fontWeight="700"
        fontFamily="monospace"
      >
        {label}
      </text>
    );
  }

  // ───── NAV BUTTON ───────────────────────────────────────────────────────────
  // A flat clickable button (not tag-bound) that jumps the Viewer to another saved
  // screen, or back to whichever screen was open before it — the click handling
  // itself lives in HMIViewer (it owns which screen is active), this is just the
  // static visual plus a hint of which direction the button points.
  if (type === 'nav-button') {
    const isBack = comp.navTargetLayoutId === '__back__';
    const isUnset = !comp.navTargetLayoutId;
    const fill = isUnset ? '#475569' : color;
    const fillDark = blendColor(fill, '#000000', 0.35);
    return (
      <g>
        <rect
          x={0} y={0} width={W} height={H} rx={Math.min(10, H * 0.22)}
          fill={fill} stroke={fillDark} strokeWidth={1.5}
          opacity={isUnset ? 0.55 : 1}
        />
        <text
          x={W / 2} y={H / 2 + (comp.fontSize || 14) * 0.35}
          textAnchor="middle"
          fill="#ffffff"
          fontSize={comp.fontSize || 14}
          fontWeight="700"
          fontFamily="monospace"
        >
          {isBack ? `← ${label}` : `${label} →`}
        </text>
        {isUnset && (
          <text x={W / 2} y={H + 12} textAnchor="middle" fill="#f87171" fontSize={9} fontFamily="monospace">
            No target screen set
          </text>
        )}
      </g>
    );
  }

  return null;
}

// ───── Helpers ─────────────────────────────────────────────────────────────────

function bX(cx: number, R: number, dir: number) {
  return cx + dir * R;
}

/**
 * Generate a SVG path for a full sine-wave water fill.
 * Period is fixed at 40px SVG units — matches hmi-water-w* CSS keyframes which
 * translate exactly -40px per cycle, creating a seamless loop regardless of tank size.
 */
function waveD(startX: number, y: number, amp: number, cycles: number, bottom: number): string {
  const period = 40;
  let d = `M ${startX},${y}`;
  for (let i = 0; i < cycles; i++) {
    const x  = startX + i * period;
    const cp = period / 4;
    // peak
    d += ` C ${x + cp},${y - amp} ${x + period / 2 - cp},${y - amp} ${x + period / 2},${y}`;
    // trough
    d += ` C ${x + period / 2 + cp},${y + amp} ${x + period - cp},${y + amp} ${x + period},${y}`;
  }
  d += ` L ${startX + cycles * period},${bottom} L ${startX},${bottom} Z`;
  return d;
}
